import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import {ReactElement} from "react";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {SwitchField} from "../model/SwitchField";
import {FormControlLabel, Switch} from "@mui/material";
import {UiComponent} from "../model/UiComponent";
import {ValuePersister} from "./ValuePersister";
import {PropertyValuePersister} from "./PropertyValuePersister";
import elementService from "../../../elementdetail/service/ElementService";
import {t} from "../../../../../../store/localization/TranslMessasge";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";

export class SwitchFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["SwitchField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValue(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return "";
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const switchField = component as SwitchField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[switchField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        let value = stateContext.values[switchField.attributeDefinitionId];
        if (value !== undefined && value !== null) {
            value = value.toString();
        }
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, stateContext, dataContext}: UiComponentRenderingProps): ReactElement {
        const switchField = component as SwitchField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[switchField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={switchField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[switchField.attributeDefinitionId] === "true";
        const readOnly = switchField.readOnly || !stateContext.editMode;

        if (!readOnly && stateContext.values[switchField.attributeDefinitionId] === undefined) {
            stateContext.onValuesChange({[switchField.attributeDefinitionId]: value});
        }

        function onChange(checked: boolean) {
            if (!readOnly) {
                stateContext.onValuesChange({[switchField.attributeDefinitionId]: "" + checked})
            }
        }

        return (
                <FormControlLabel
                        key={switchField.id}
                        label={attributeDefinition.name}
                        control={
                            <Switch
                                    checked={value}
                                    onChange={(event, checked) => onChange(checked)}
                                    required={attributeDefinition.required}
                                    readOnly={readOnly}
                            />
                        }
                />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const switchField = component as SwitchField;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[switchField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[switchField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}